import {
  getIsOfficialAccountById,
  request,
  queryClient,
  getCurrentAddress,
  debounce,
  isSystemAddress,
  getRelativeTime,
} from '@/utils';
import { useQuery, useInfiniteQuery, useMutation } from 'react-query';
import { HiSession, HiSessionLocal } from '../types';
import { useCallback, useEffect, useState, useRef } from 'react';
import { convertHiMessageToLocal, getLastMessageTimeFromSession } from '../utils';
import { useCurrentAddressInfo, useHiUserCache, useWindowVisible } from '@/hooks';

const SESSION_PAGE_COUNT = 15;

export const useHiSessionWith = (
  {
    session_id,
    other_side_id,
    polling,
  }: {
    other_side_id?: string;
    session_id?: HiSession['id'];
    polling?: boolean;
  },
  {
    page_key = '',
  }: {
    page_key?: string | number;
  } = {},
  disabled?: boolean,
) => {
  const { address: meId } = useCurrentAddressInfo();

  return useQuery(
    ['/hi/session', other_side_id, meId],
    () =>
      request.get<HiSession>('/hi/session', {
        ...(session_id ? { id: session_id } : { other_user_id: other_side_id! }),
      }),
    {
      enabled: (!!other_side_id || !!session_id) && !disabled,
      refetchInterval: polling ? 1000 * 4 : false,
    },
  );
};

export const invalidHiSession = (
  otherSideHiId: string,
  hiSession: Partial<HiSession>,
  isRemoved?: boolean,
) => {
  const myId = getCurrentAddress();

  queryClient.setQueryData(['/hi/session', otherSideHiId, myId], (oldData: any) => {
    if (isRemoved) {
      return null;
    }
    return {
      ...oldData,
      ...hiSession,
    };
  });
};

export const invalidHiSessionList = debounce(() => {
  const myId = getCurrentAddress();
  queryClient.invalidateQueries([sessionListQueryUrl, myId]);
}, 1000);

const sessionListQueryUrl = '/hi/session/list';

type SessionPageType = {
  session_list: HiSession[];
  user_dict: Record<string, UserInfo | OfficialAccount>;
};

export const useInfiniteHiSessionList = (
  start: number = 0,
  limit: number = SESSION_PAGE_COUNT,
  disabled = false,
) => {
  const { address: myId } = useCurrentAddressInfo();

  const { userDict, updateHiUserCache } = useHiUserCache();

  const tempUserDict = useRef({ ...userDict });

  useEffect(() => {
    tempUserDict.current = { ...userDict };
  }, [userDict]);

  const query = useInfiniteQuery<HiSessionLocal[]>(
    [sessionListQueryUrl, myId],
    async ({ pageParam }) => {
      let requestParam: {
        limit: number;
        start: number;
      } = {
        start,
        limit,
      };

      if (pageParam) {
        requestParam = {
          ...requestParam,
          ...pageParam,
        };
      }

      const data = await request.get<SessionPageType>(sessionListQueryUrl, requestParam);

      const { session_list = [], user_dict = {} } = data;
      session_list.forEach(session => {
        const userId = session.other_user_id;

        if (user_dict[userId]) {
          tempUserDict.current[userId] = user_dict[userId];
        } else {
          session.is_active = false;
          console.log('invalid user info');
        }
      });
      updateHiUserCache(tempUserDict.current);
      return (session_list || []).map(hiSession => {
        const isSystemAccount = isSystemAddress(hiSession.other_user_id);
        const isOfficialAccount = getIsOfficialAccountById(hiSession.other_user_id);
        const isCommontAccount = !isSystemAccount && !isOfficialAccount;
        const lastMsgTime = getLastMessageTimeFromSession(hiSession);

        return {
          ...hiSession,
          other_side: isCommontAccount ? tempUserDict.current?.[hiSession.other_user_id] : null,
          other_side_official_account: isOfficialAccount
            ? tempUserDict.current?.[hiSession.other_user_id]
            : null,
          other_side_system_account: isSystemAccount
            ? tempUserDict.current?.[hiSession.other_user_id]
            : null,
          latest_preview_msg: hiSession.last_display_message
            ? convertHiMessageToLocal(hiSession.last_display_message, {
                session_id: hiSession.id,
                other_side_id: hiSession.other_user_id,
                my_id: myId!,
              })
            : null,
          latest_msg_time: lastMsgTime,
          lastMsgTimeStr: getRelativeTime(lastMsgTime, { fullFormat: 'MMM DD' }),
        } as HiSessionLocal;
      });
    },
    {
      enabled: !!myId && !disabled,
      staleTime: 0,
      cacheTime: 5000,

      getNextPageParam: (lastPage: HiSessionLocal[], pages) => {
        if (!lastPage || !lastPage.length) return;

        if (lastPage.length === SESSION_PAGE_COUNT) {
          return {
            start: pages.length * SESSION_PAGE_COUNT,
          };
        }
        return;
      },
      getPreviousPageParam: () => {
        // fetch first page
        return {
          start: 0,
        };
      },
    },
  );
  return query;
};

export function useHiConversationList() {
  const isWindowVisible = useWindowVisible();

  const { data, ...loadingQuery } = useInfiniteHiSessionList(
    0,
    SESSION_PAGE_COUNT,
    !isWindowVisible,
  );
  const { address: myId } = useCurrentAddressInfo();
  const [sessionLocalList, setSessionLocalList] = useState<HiSessionLocal[]>([]);

  const setLocalData = useCallback(() => {
    const list = data?.pages
      .flat(1)
      .reverse()
      .filter(session => Boolean(session.is_active));

    setSessionLocalList(list || []);
  }, [data?.pages]);

  useEffect(() => {
    return () => {
      if (!myId) return;
      if (data) {
        data.pageParams = [];
        data.pages = [];
        setLocalData();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId]);

  useEffect(() => {
    setLocalData();
  }, [data?.pages, setLocalData]);

  const refreshAllPage = useCallback(() => {
    isWindowVisible && invalidHiSessionList();
  }, [isWindowVisible]);

  return {
    data: sessionLocalList,
    refreshAllPage,
    ...loadingQuery,
  };
}

export function useAddSession() {
  return useMutation(({ otherSideId }: { otherSideId: string }) => {
    return request
      .post<{ session: HiSession }>(`/hi/session/init`, {
        other_user_id: otherSideId,
      })
      .then(
        data => {
          if (data.session) {
            invalidHiSession(otherSideId, data?.session);
            invalidHiSessionList();
          }

          return data;
        },
        (err_msg: string) => {
          console.warn(err_msg);
        },
      );
  });
}

export function usePinSession() {
  return useMutation(
    ({
      sessionId,
      pinned,
    }: {
      sessionId: HiSession['id'];
      pinned?: boolean;
      archive?: boolean;
    }) => {
      return request
        .post<{ is_success: boolean }>(`/hi/session/${pinned ? 'pin' : 'unpin'}`, {
          id: sessionId,
        })
        .then(
          data => {
            if (data.is_success) {
              invalidHiSessionList();
            }
            return data.is_success;
          },
          (err_msg: string) => {
            console.warn(err_msg);
            return false;
          },
        );
    },
  );
}

export function useArchiveSession() {
  return useMutation(
    ({ sessionId, otherSideHiId }: { sessionId: HiSession['id']; otherSideHiId: string }) => {
      return request
        .post<{ is_success: boolean }>(`/hi/session/archive`, {
          id: sessionId,
        })
        .then(
          data => {
            if (data.is_success) {
              invalidHiSession(otherSideHiId, { is_active: false });
              invalidHiSessionList();
            }
            return data?.is_success;
          },
          (err_msg: string) => {
            console.warn(err_msg);
            return false;
          },
        );
    },
  );
}
